import { Link } from "react-router-dom";
import { Mail, Quote, Target, Telescope, ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal, Marquee } from "@/components/Reveal";
import { COMPANY, VALUES, TECH } from "@/data/site";

export default function About() {
  return (
    <>
      <Seo
        title="About LKTH LTD | UK IT Consultancy Based in Sheffield"
        description="LKTH LTD is a Sheffield-based IT services company founded by Latesh Katravath, building secure, scalable web portals and IT solutions for UK businesses."
      />

      {/* Hero */}
      <section className="relative overflow-hidden bg-[#0B1F3A] pt-[72px]">
        <div className="absolute inset-0 grid-bg" aria-hidden="true" />
        <div className="absolute inset-0 hero-glow" aria-hidden="true" />
        <div className="container-x relative py-16 sm:py-24">
          <p className="eyebrow eyebrow-light font-mono-alt">About Us</p>
          <h1 className="mt-4 max-w-2xl font-display text-4xl font-bold text-white sm:text-5xl">
            Enterprise-grade IT, without the enterprise ego
          </h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg">
            LKTH LTD was founded in Sheffield on a simple belief: businesses of every size deserve
            software built properly, priced fairly and supported by people who answer the phone.
          </p>
        </div>
      </section>

      {/* Story + mission/vision */}
      <section className="section-pad bg-white">
        <div className="container-x grid items-center gap-14 lg:grid-cols-2">
          <Reveal>
            <div className="relative overflow-hidden rounded-2xl">
              <img
                src="https://images.unsplash.com/photo-1568992687947-868a62a9f521?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400"
                alt="The LKTH team planning a client portal project together"
                loading="lazy"
                className="aspect-[4/3] w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B1F3A]/50 to-transparent" aria-hidden="true" />
              <p className="absolute bottom-5 left-5 right-5 font-mono-alt text-xs uppercase tracking-[0.2em] text-white/90">
                Based in Sheffield · Working across the UK
              </p>
            </div>
          </Reveal>
          <div>
            <Reveal>
              <p className="eyebrow font-mono-alt">Our story</p>
              <h2 className="mt-4 font-display text-3xl font-bold text-[#0B1F3A]">
                Built in Sheffield, trusted across the UK
              </h2>
              <p className="mt-5 text-base leading-relaxed text-slate-600">
                LKTH LTD started with founder Latesh Katravath and a laptop, delivering websites and
                portals for local businesses. As word spread, the company grew into a full IT
                services partner — but the way we work has not changed: listen carefully, scope
                honestly, build well and stay reachable.
              </p>
              <p className="mt-4 text-base leading-relaxed text-slate-600">
                Today we design and develop web portals, websites and applications; run cloud
                infrastructure; protect business data; and provide friendly, UK-based IT support for
                companies that cannot afford downtime.
              </p>
            </Reveal>
            <div className="mt-8 grid gap-4 sm:grid-cols-2">
              <Reveal delay={0.05}>
                <div className="card-soft h-full p-6">
                  <Target className="h-5 w-5 text-blue-600" aria-hidden="true" />
                  <h3 className="mt-3 font-display text-base font-semibold text-[#0B1F3A]">Our Mission</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">
                    To build secure, scalable web portals and IT solutions that let ambitious UK
                    businesses work smarter and grow with confidence.
                  </p>
                </div>
              </Reveal>
              <Reveal delay={0.1}>
                <div className="card-soft h-full p-6">
                  <Telescope className="h-5 w-5 text-blue-600" aria-hidden="true" />
                  <h3 className="mt-3 font-display text-base font-semibold text-[#0B1F3A]">Our Vision</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">
                    To be the North of England's most trusted IT partner — known for engineering
                    quality, honesty and support that genuinely supports.
                  </p>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section-pad bg-[#F8FAFC]" data-testid="about-values">
        <div className="container-x">
          <Reveal className="text-center">
            <p className="eyebrow font-mono-alt">Core Values</p>
            <h2 className="mx-auto mt-4 max-w-lg font-display text-3xl font-bold text-[#0B1F3A] sm:text-4xl">
              The principles behind every build
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={0.06 * i} className="h-full">
                <div className="card-soft h-full p-7">
                  <span className="font-mono-alt text-sm font-bold text-blue-600/60">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="mt-3 font-display text-lg font-semibold text-[#0B1F3A]">{v.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Tech marquee */}
      <section className="border-y border-slate-200 bg-white py-8" aria-label="Technologies we work with">
        <Marquee
          items={TECH}
          duration={40}
          render={(t) => (
            <span key={t} className="mx-5 inline-flex items-center font-mono-alt text-sm font-medium text-slate-500">
              {t}
              <span className="ml-10 h-1 w-1 rounded-full bg-blue-500/50" aria-hidden="true" />
            </span>
          )}
        />
      </section>

      {/* Founder — no photo by design */}
      <section className="section-pad bg-white" data-testid="about-founder">
        <div className="container-x">
          <Reveal>
            <div className="relative overflow-hidden rounded-2xl bg-[#0B1F3A] p-8 sm:p-12">
              <div className="absolute inset-0 grid-bg" aria-hidden="true" />
              <div className="absolute inset-0 hero-glow" aria-hidden="true" />
              <div className="relative grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:gap-16">
                <div>
                  <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500 to-blue-700 font-display text-xl font-bold text-white">
                    LK
                  </div>
                  <p className="eyebrow eyebrow-light font-mono-alt mt-6">Founder</p>
                  <h2 className="mt-3 font-display text-3xl font-bold text-white">Latesh Katravath</h2>
                  <p className="mt-1 text-sm font-medium text-blue-300">Founder &amp; Principal Consultant</p>
                  <p className="mt-5 text-sm leading-relaxed text-slate-300">
                    Latesh works directly with every LKTH client — from the first scoping call to
                    launch and beyond — and leads the company's security-first approach to every
                    build. He is your point of contact, and he answers his own emails.
                  </p>
                  <a
                    href={`mailto:${COMPANY.email}`}
                    className="mt-6 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:border-blue-400 hover:text-blue-300"
                    data-testid="founder-email-link"
                  >
                    <Mail className="h-4 w-4" aria-hidden="true" /> Email Latesh
                  </a>
                </div>
                <figure className="flex flex-col justify-center rounded-xl border border-white/10 bg-white/5 p-8 backdrop-blur">
                  <Quote className="h-6 w-6 text-blue-400" aria-hidden="true" />
                  <blockquote className="mt-4 font-display text-xl font-medium leading-relaxed text-white sm:text-2xl">
                    A portal should do more than look good on launch day. It should still be fast,
                    secure and easy to use five years from now — that is what we build for.
                  </blockquote>
                  <figcaption className="mt-5 font-mono-alt text-xs uppercase tracking-[0.2em] text-slate-400">
                    — Latesh Katravath, Founder, LKTH LTD
                  </figcaption>
                </figure>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-[#F8FAFC] py-14">
        <div className="container-x text-center">
          <h2 className="font-display text-2xl font-bold text-[#0B1F3A]">Let's build something that lasts</h2>
          <Link to="/contact" className="btn-primary mt-6" data-testid="about-cta">
            Get in Touch <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </>
  );
}
